import React from 'react';
import { ALL_KEYS } from '../../lib/theory';
import { Tray } from './Tray';

const TONALITY_OPTIONS = ['Major', 'Minor'];

/**
 * The top of every instrument's Key card: the 6×2 key grid, then Major/Minor and Structure.
 *
 * Phone: Major/Minor and Structure share one row (board 18) when Structure has up to 4 options;
 * with 5 or more they stack so the short labels still fit. Desktop: each tray gets its own row,
 * Structure in 2 columns (3 when it has 3 or 6+ options, so no option sits alone on a row). With
 * exactly 5 it is a 6-column grid: three options on the first row, two wider ones on the second,
 * so a long desktop label ("Double Stops", "Root & 5th") gets half the row instead of wrapping.
 * List the long labels last in `structureOptionsDesktop` so they land on the wide row.
 *
 * Major/Minor is a switch: tapping either side flips it (tapping Minor while on Minor goes to Major).
 *
 * Follow-up trays (modes, arpeggio types, shapes, intervals) go after this, inside the same card.
 *
 * @param {object} props
 * @param {boolean} props.isDesktop From useIsDesktop().
 * @param {string} props.keyValue
 * @param {(key: string) => void} props.onKeyChange
 * @param {'Major'|'Minor'} props.tonality
 * @param {(tonality: string) => void} props.onTonalityChange
 * @param {boolean} [props.tonalityDisabled] e.g. while Modes is on (the mode sets the quality).
 * @param {Array<{value: string, label: string}>} props.structureOptions Phone labels (short).
 * @param {Array<{value: string, label: string}>} [props.structureOptionsDesktop] Desktop labels; defaults to phone ones.
 * @param {string} props.structure
 * @param {(structure: string) => void} props.onStructureChange
 */
export function KeyRows({
  isDesktop,
  keyValue, onKeyChange,
  tonality, onTonalityChange, tonalityDisabled = false,
  structureOptions, structureOptionsDesktop, structure, onStructureChange,
}) {
  const options = isDesktop ? (structureOptionsDesktop ?? structureOptions) : structureOptions;
  const many = options.length > 4;
  // Desktop, 5 options: 3 + 2 on a 6-column grid (see above).
  const fiveWide = isDesktop && options.length === 5;
  const structureItems = fiveWide
    ? options.map((option, i) => ({ ...option, span: i < 3 ? 2 : 3 }))
    : options;

  const tonalityTray = (
    <Tray
      ariaLabel="Tonality"
      options={TONALITY_OPTIONS}
      value={tonality}
      // It works as a switch: tapping the side that is already on flips it to the other one.
      onChange={(next) => onTonalityChange(next === tonality ? (tonality === 'Major' ? 'Minor' : 'Major') : next)}
      columns={2}
      slide
      size={isDesktop ? 'md' : 'sm'}
      disabled={tonalityDisabled}
    />
  );

  const structureTray = (
    <Tray
      ariaLabel="Structure"
      options={structureItems}
      value={structure}
      onChange={onStructureChange}
      columns={isDesktop ? (fiveWide ? 6 : options.length === 3 || many ? 3 : 2) : options.length}
      size={isDesktop ? 'md' : 'xs'}
    />
  );

  return (
    <>
      <Tray
        ariaLabel="Key"
        options={ALL_KEYS}
        value={keyValue}
        onChange={onKeyChange}
        columns={6}
        size="md"
      />
      {isDesktop || many ? (
        <>
          {tonalityTray}
          {structureTray}
        </>
      ) : (
        <div className="flex gap-1.5">
          <div className="min-w-0 flex-[2]">{tonalityTray}</div>
          <div className="min-w-0 flex-[3.4]">{structureTray}</div>
        </div>
      )}
    </>
  );
}
